/**
 * Which picture means what, in exactly one place (Redesign).
 *
 * THE ICON IS A SECOND RENDERING OF A VALUE THE BACKEND ALREADY SENT, never a
 * fact of its own. `category`, `trigger_type` and `runtime_mode` each have a
 * German word in `automationLabels` and a glyph here, and the two are chosen
 * from the SAME field. A row whose envelope disagreed with its "E-Mails" label
 * would be a contradiction a reader cannot resolve, so both lookups take the
 * raw value and neither derives anything.
 *
 * AN UNKNOWN VALUE GETS A NEUTRAL GLYPH, not a guess. `automationLabels` falls
 * through to the raw string for a vocabulary the backend has grown and the
 * frontend has not learned yet; the visual equivalent is a generic mark, which
 * is honest about not knowing. Picking "probably an email" for an unknown
 * category would assert something no response said.
 *
 * ICONS ARE DECORATION AND ARE MARKED AS SUCH. Every glyph here sits beside the
 * word it illustrates, so all of them are `aria-hidden`: a screen reader that
 * announced "Briefumschlag E-Mails" would hear the same fact twice. The text is
 * what carries the meaning — the same rule `automationLabels` records for its
 * badge tones (§36).
 */
import React from "react";
import {
  BarChart3,
  Calendar,
  CheckCircle2,
  CircleHelp,
  CircleSlash,
  Clock,
  Hand,
  History,
  Mail,
  MapPin,
  MousePointerClick,
  PencilLine,
  PowerOff,
  SkipForward,
  XCircle,
  Zap,
} from "lucide-react";

import { automationIconToneClass } from "./automationLabels";

/** `category` — the registry's grouping. */
const CATEGORY_ICONS = {
  email: Mail,
  // The move-in address adoption: a place, not a message.
  tenant_data: MapPin,
};

/** `trigger_type` — what starts the automation. */
const TRIGGER_ICONS = {
  date: Calendar,
  manual: MousePointerClick,
  schedule: Clock,
  test: Zap,
};

/** `runtime_mode` — how it is reached. NOT whether it is switched on. */
const RUNTIME_MODE_ICONS = {
  scheduled: Clock,
  manual_only: Hand,
  // PC4: registered so its template can be written, and nothing runs it yet.
  preparing: PencilLine,
};

/** `action_type` — what happens once it is triggered (D9, Auslöser). */
const ACTION_ICONS = {
  send_email: Mail,
  update_tenant_address: MapPin,
};

/**
 * The 30-day outcome counts. `total` is the tally, not an outcome; since D11b
 * `indeterminate` has its own card and a question mark — unknown, not wrong.
 */
const STAT_ICONS = {
  succeeded: CheckCircle2,
  failed: XCircle,
  skipped: SkipForward,
  indeterminate: CircleHelp,
  total: BarChart3,
};

/** Status glyphs, paired with the words in `AutomationStatusPill`. */
export const STATUS_ICONS = {
  active: CheckCircle2,
  off: PowerOff,
  manual: Hand,
  preparing: PencilLine,
  unknown: CircleSlash,
};

export const LAST_RUN_ICON = History;

export function automationCategoryIcon(value) {
  return CATEGORY_ICONS[value] || Zap;
}

export function automationTriggerIcon(value) {
  return TRIGGER_ICONS[value] || Zap;
}

export function automationRuntimeModeIcon(value) {
  return RUNTIME_MODE_ICONS[value] || Zap;
}

export function automationActionIcon(value) {
  return ACTION_ICONS[value] || Zap;
}

export function automationStatIcon(value) {
  return STAT_ICONS[value] || BarChart3;
}

const TILE_SIZES = {
  sm: "h-7 w-7 rounded-[8px]",
  md: "h-10 w-10 rounded-[10px]",
};

const GLYPH_SIZES = {
  sm: "h-3.5 w-3.5",
  md: "h-[18px] w-[18px]",
};

/**
 * A glyph on a tinted square — the shape the list rows, the detail header and
 * the statistics all use.
 *
 * ONE COMPONENT RATHER THAN THREE COPIES OF THE SAME FIVE CLASSES: the tile is
 * the redesign's most repeated element, and a size or radius that drifted in
 * one of the three places is precisely the kind of difference nobody sees in a
 * diff but everybody sees on the page.
 */
export function AutomationIconTile({
  icon: Icon,
  tone = "neutral",
  size = "md",
  className = "",
}) {
  if (!Icon) return null;
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${
        TILE_SIZES[size] || TILE_SIZES.md
      } ${automationIconToneClass(tone)} ${className}`}
    >
      <Icon className={GLYPH_SIZES[size] || GLYPH_SIZES.md} aria-hidden="true" />
    </span>
  );
}
